import { cn } from '@neo/lib/utils'
import { domAnimation, LazyMotion, m, useReducedMotion } from 'framer-motion'
import type { FC, ReactNode } from 'react'
import { EASE, FADE_S, HIGHLIGHT, POP, POP_IN, useHasMounted } from './motion'

/**
 * Brings in what replaced the last `id`: it pops (`pop`, for a glyph) or fades in over the old.
 * `highlight`, for a status a poll changed, also lays a tint behind it that fades away. Not on first
 * show, unless `isNew` (it mounted with what it marks). Reduced motion: fades.
 */
export const ChangeIn: FC<{
  id: string
  pop?: boolean
  highlight?: boolean
  isNew?: boolean
  /** A div, for block content. */
  block?: boolean
  className?: string
  children: ReactNode
}> = ({
  id,
  pop = false,
  highlight = false,
  isNew = false,
  block = false,
  className = 'flex',
  children,
}) => {
  const isChange = useHasMounted() || isNew
  const isStatic = useReducedMotion()
  const Tag = block ? m.div : m.span
  return (
    <LazyMotion features={domAnimation}>
      <Tag
        key={id}
        className={cn(highlight && 'relative isolate', className)}
        initial={isChange ? (pop && !isStatic ? POP_IN : { opacity: 0 }) : false}
        animate={{ scale: 1, opacity: 1 }}
        transition={pop ? POP : { duration: FADE_S, ease: EASE.out }}
      >
        {highlight && (
          // A first show mounts it already faded.
          <m.span
            className="pointer-events-none absolute -inset-x-1.5 -inset-y-0.5 -z-10 rounded-md bg-brand/10"
            initial={{ opacity: isChange ? 1 : 0 }}
            animate={{ opacity: 0 }}
            transition={HIGHLIGHT}
            aria-hidden
          />
        )}
        {children}
      </Tag>
    </LazyMotion>
  )
}
